import { createSelector } from '@reduxjs/toolkit';
import { renderInputComponent } from '@ticketmaster/tm1pos-web-shared/components/InputComponent';
import { PaymentType } from '@ticketmaster/tm1pos-web-shared/model/payment';
import { type PaymentMethod } from '@ticketmaster/tm1pos-web-shared/model/permissions';
import { useCallback, useEffect, useState } from 'react';
import { type InjectedIntl, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { change, Field, formValueSelector } from 'redux-form/immutable';
import {
  ARCHTICS_EVENT_TYPE,
  ARCHTICS_PAYMENT_INFO_LIMITATION,
  HOST_PAYMENT_INFO_LIMITATION,
} from '../../../../../../constants';
import { getPaymentMethodMessageByKey } from '../../../../../../models/payment-methods-messages';
import { type AppStore } from '../../../../../../store';
import { renderDropdownComponent } from '../../../../../App/components/SearchFilterDropdown';
import { selectCurrentEvent } from '../../../../../EventDetailPage/selectors/main';
import messages from '../../../../messages';
import { type FieldNames } from '../../checkout-form';
import {
  CASH_PAYMENT_INFO_CATEGORY,
  CHECK_PAYMENT_INFO_CATEGORY,
  CHECKOUT_FORM_TITLE,
  COMP,
  CREDIT_PAYMENT_INFO_CATEGORY,
  NO_PAYMENT_INFO_CATEGORY,
} from '../../constants';
import { selectOtherPaymentMethods } from '../../selectors';
import { CashForm } from '../cash-form/cash-form';
import './other-form.scss';

const validatorsWithIntl = (intl: InjectedIntl) => ({
  paymentInfoCheckValidation: (value: string) => {
    if (!value) {
      return undefined;
    }
    const letterNumber = /^[0-9a-zA-Z]+$/;
    if (value.length > 10) {
      return intl.formatMessage(messages.paymentInfoMax10Chars);
    }
    if (value.match(letterNumber)) {
      return undefined;
    }
    return intl.formatMessage(messages.paymentInfoMax10Chars);
  },
  paymentInfoCreditCardValidation: (value: string | number) => {
    if (value === 0 || !value || value === '0') {
      return undefined;
    }
    // @ts-expect-error - value can be a number, but parseInt expects a string
    return /\d{12}/.exec(parseInt(value, 10)) ? undefined : intl.formatMessage(messages.paymentInfo12Digits);
  },
  paymentInfoValidation: (value: string, currentEvent: ReturnType<typeof selectCurrentEvent>) => {
    const maxValue =
      currentEvent.type === ARCHTICS_EVENT_TYPE ? ARCHTICS_PAYMENT_INFO_LIMITATION : HOST_PAYMENT_INFO_LIMITATION;
    return value && value.length > maxValue
      ? [
          intl.formatMessage(messages.paymentInfoLimitMsg),
          intl.formatMessage(messages.paymentInfoMaxValue, { maxValue }),
        ]
      : undefined;
  },
  validatePaymentMethod: (value: string | undefined) =>
    value ? undefined : intl.formatMessage(messages.paymentInfoRequired),
});

export type PaymentMethods = PaymentMethod[];

type OtherFormProps = {
  paymentInfoLabel: string;
  otherPaymentsLabel: string;
  cashToTender: {
    amount: number;
    currency: string;
  };
  fieldNames: FieldNames;
  intl: InjectedIntl;
};

const OtherFormComponent = (props: OtherFormProps) => {
  const [activeCategory, setActiveCategory] = useState('');

  const dispatch = useDispatch();

  const currentEvent = useSelector(selectCurrentEvent);

  const filterNonApplicablePaymentMethods = (paymentMethods: PaymentMethods) =>
    paymentMethods.filter((method) => method.type !== PaymentType.PIX);

  const otherPaymentMethods =
    useSelector(createSelector(selectOtherPaymentMethods, filterNonApplicablePaymentMethods)) || [];

  const selector = formValueSelector(CHECKOUT_FORM_TITLE);

  const getFieldValue = useSelector(
    createSelector(
      [(state: AppStore) => state],
      (state: AppStore) => (fieldName: string) => selector(state, fieldName),
    ),
  );

  const translatePaymentMethod = (type: PaymentType, fallback: string) =>
    props.intl.formatMessage(
      getPaymentMethodMessageByKey(type) || {
        id: 'fake id',
        defaultMessage: fallback,
      },
    );

  type MapMethodToInputResult = ReturnType<typeof mapMethodToInput>;

  const mapMethodToInput = (method: PaymentMethod) => {
    const translatedLabel = translatePaymentMethod(method.type as PaymentType, method.description);
    return {
      id: method.type,
      name: translatedLabel,
      value: method.type,
      label: translatedLabel,
      disabled: false,
      hasError: false,
    };
  };

  const handleChange = (method: MapMethodToInputResult | { value: string }) => {
    const category = otherPaymentMethods.find((it: PaymentMethod) => it.type === method.value)?.category;

    if (typeof category === 'string') {
      setActiveCategory(category);
    }
  };

  function setFieldDefaultInputValue(defaultInputValue: MapMethodToInputResult) {
    const { fieldNames } = props;
    dispatch(change(CHECKOUT_FORM_TITLE, fieldNames.otherPaymentMethod, defaultInputValue));
    handleChange({ value: defaultInputValue.value });
  }

  useEffect(() => {
    const defaultPaymentInfo =
      props.cashToTender?.amount === 0
        ? otherPaymentMethods.find((method) => COMP.indexOf(method.type) !== -1)
        : otherPaymentMethods.find((method) => CASH_PAYMENT_INFO_CATEGORY.indexOf(method.category) !== -1);

    if (defaultPaymentInfo) {
      const initialValue = mapMethodToInput(defaultPaymentInfo);
      setFieldDefaultInputValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validators = useCallback(() => validatorsWithIntl(props.intl), [props.intl]);

  const paymentInfoCheckValidation = useCallback(
    (value) => validators().paymentInfoCheckValidation(value),
    [validators],
  );

  const paymentInfoCreditCardValidation = useCallback(
    (value) => validators().paymentInfoCreditCardValidation(value),
    [validators],
  );

  const paymentInfoValidation = useCallback(
    (value) => validators().paymentInfoValidation(value, currentEvent),
    [validators, currentEvent],
  );

  const validatePaymentMethod = useCallback((value) => validators().validatePaymentMethod(value), [validators]);

  function paymentInfo() {
    const { fieldNames, cashToTender, paymentInfoLabel, intl } = props;
    if (NO_PAYMENT_INFO_CATEGORY.indexOf(activeCategory) !== -1 || !activeCategory) {
      return null;
    }

    if (CASH_PAYMENT_INFO_CATEGORY.indexOf(activeCategory) !== -1) {
      return <CashForm fieldNames={fieldNames} cashToTender={cashToTender} getFieldValue={getFieldValue} />;
    }

    if (CHECK_PAYMENT_INFO_CATEGORY.indexOf(activeCategory) !== -1) {
      return (
        <Field
          name={fieldNames.paymentInfo}
          component={renderInputComponent}
          placeholder={paymentInfoLabel}
          validate={paymentInfoCheckValidation}
        />
      );
    }

    if (CREDIT_PAYMENT_INFO_CATEGORY.indexOf(activeCategory) !== -1) {
      return (
        <Field
          name={fieldNames.paymentInfo}
          component={renderInputComponent}
          placeholder={intl.formatMessage(messages.cardNumberPlaceholder)}
          validate={paymentInfoCreditCardValidation}
        />
      );
    }

    return (
      <Field
        name={fieldNames.paymentInfo}
        component={renderInputComponent}
        placeholder={paymentInfoLabel}
        validate={paymentInfoValidation}
      />
    );
  }

  const { fieldNames } = props;

  const provider = otherPaymentMethods.map(({ description, type }) => ({
    label: translatePaymentMethod(type as PaymentType, description),
    value: type,
  }));

  return (
    <div className="other-entry__form">
      <Field
        name={fieldNames.otherPaymentMethod}
        component={renderDropdownComponent}
        dataProvider={provider}
        emptySelection={props.otherPaymentsLabel}
        minimumOptionsToDisplaySearch={10}
        searchPlaceholder={props.intl.formatMessage(messages.otherPaymentsSearchPlaceholder)}
        emptySearchMessage={messages.noPaymentMethodsFound}
        validate={validatePaymentMethod}
        // @ts-expect-error - TS expects that whatever is passed to `onChange` should be an event
        // object. However, logging the result passed to `handleChange`, it is clear that it receives
        // a MapMethodToInputResult as the argument.
        onChange={handleChange}
      />
      {paymentInfo()}
    </div>
  );
};

export const OtherForm = injectIntl(OtherFormComponent);

export const validatorsForUnitTestingPurposes = validatorsWithIntl;
